import React, { useState, useEffect } from 'react';
import { X, Users, FileText } from 'lucide-react';

const GroupModal = ({ isOpen, onClose, onSave, group }) => {
  const [formData, setFormData] = useState({
    nom: '',
    description: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (group) {
      setFormData({
        nom: group.nom || '',
        description: group.description || ''
      });
    } else {
      setFormData({
        nom: '',
        description: ''
      });
    }
    setErrors({});
  }, [group, isOpen]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.nom.trim()) {
      newErrors.nom = 'Le nom du groupe est obligatoire';
    }

    if (formData.nom.length > 50) {
      newErrors.nom = 'Le nom ne peut pas dépasser 50 caractères';
    }

    if (formData.description.length > 200) {
      newErrors.description = 'La description ne peut pas dépasser 200 caractères';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Effacer l'erreur quand l'utilisateur commence à taper
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-secondary-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">
          &#8203;
        </span>

        <div className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:align-middle">
          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-secondary-900">
                  {group ? 'Modifier le groupe' : 'Nouveau groupe'}
                </h3>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-secondary-400 hover:text-secondary-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700">
                    Nom du groupe *
                  </label>
                  <div className="mt-1 relative">
                    <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
                    <input
                      type="text"
                      className={`input pl-10 ${errors.nom ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                      value={formData.nom}
                      onChange={(e) => handleInputChange('nom', e.target.value)}
                      placeholder="Ex: Clients VIP"
                      maxLength={50}
                    />
                  </div>
                  {errors.nom && (
                    <p className="mt-1 text-sm text-red-600">{errors.nom}</p>
                  )}
                  <p className="mt-1 text-xs text-secondary-500">
                    {formData.nom.length}/50 caractères
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700">
                    Description
                  </label>
                  <div className="mt-1 relative">
                    <FileText className="absolute left-3 top-3 h-4 w-4 text-secondary-400" />
                    <textarea
                      className={`input pl-10 ${errors.description ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      placeholder="Description du groupe (optionnel)"
                      rows={3}
                      maxLength={200}
                    />
                  </div>
                  {errors.description && (
                    <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                  )}
                  <p className="mt-1 text-xs text-secondary-500">
                    {formData.description.length}/200 caractères
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-secondary-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
              <button
                type="submit"
                className="btn btn-primary w-full sm:w-auto sm:ml-3"
              >
                {group ? 'Modifier' : 'Créer'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary w-full sm:w-auto mt-3 sm:mt-0"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default GroupModal;
